import React, { useState, useEffect } from "react";
import "./Home.css";
import API_URL from "../apiConfig";

const API_BASE_URL = API_URL;

const ProductCard = ({ product, onClick }) => {
  const imageUrl = product.images?.[0]
    ? product.images[0].startsWith("http")
      ? product.images[0]
      : `${API_BASE_URL}${product.images[0]}`
    : "https://via.placeholder.com/400x533?text=No+Image";

  // Calculate discount if originalPrice exists
  const discount =
    product.originalPrice && product.originalPrice > product.price
      ? Math.round(
          ((product.originalPrice - product.price) / product.originalPrice) *
            100,
        ) + "% OFF"
      : null;

  return (
    <div
      className="product-card"
      onClick={() => onClick(product)}
      style={{ cursor: "pointer" }}
    >
      <div className="product-image">
        <img src={imageUrl} alt={product.name} />
        <div className="product-rating">{product.rating || 4.0} ★</div>
      </div>
      <div className="product-info">
        <h3 className="product-brand">{product.brand}</h3>
        <p className="product-name">{product.name}</p>
        <div className="product-pricing">
          <span className="current-price">Rs. {product.price}</span>
          {product.originalPrice > 0 && (
            <span className="original-price">Rs. {product.originalPrice}</span>
          )}
          {discount && <span className="discount">({discount})</span>}
        </div>
      </div>
    </div>
  );
};

const DEFAULT_CATEGORIES = [
  "Kurti",
  "Lehenga",
  "Sarees",
  "Palazo sets",
  "Blouses",
  "Ethnic",
].map((name, index) => ({ _id: `default-${index}`, name }));

const Home = ({ onProductClick, searchQuery }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const productsUrl = searchQuery
          ? `${API_BASE_URL}/api/products?search=${encodeURIComponent(searchQuery)}`
          : `${API_BASE_URL}/api/products`;

        const [productsRes, categoriesRes] = await Promise.all([
          fetch(productsUrl),
          fetch(`${API_BASE_URL}/api/categories`),
        ]);

        if (productsRes.ok) {
          const productsData = await productsRes.json();
          setProducts(productsData);
        }

        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          setCategories(categoriesData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [searchQuery]);

  return (
    <div className="home-page">
      <section className="hero-banner">
        <div className="banner-item">
          <img
            src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=1280"
            alt="Hero"
          />
          <div className="banner-content">
            <h1>SUMMER COLLECTION 2026</h1>
            <p>Up to 50% OFF on Top Brands</p>
            <button className="shop-now-btn">SHOP NOW</button>
          </div>
        </div>
      </section>

      <section className="container section">
        <div className="section-header">
          <h2 className="section-title">
            {searchQuery
              ? `SEARCH RESULTS FOR "${searchQuery.toUpperCase()}"`
              : "DEALS OF THE DAY"}
          </h2>
          {searchQuery && (
            <button
              className="clear-search-btn"
              onClick={() => {
                // We need a way to clear the search query from here
                // Passing a reset function or using a global state would be better
                // For now, let's hope the prop update works
                window.dispatchEvent(new CustomEvent("clearSearch"));
              }}
            >
              Clear Search
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Finding the best matches for you...</p>
          </div>
        ) : (
          <div className="product-grid">
            {products.length > 0 ? (
              products.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onClick={onProductClick}
                />
              ))
            ) : (
              <div className="no-results-products">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ccc"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="11" y1="8" x2="11" y2="14"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
                <p>No products found matching "{searchQuery}"</p>
                <button
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent("clearSearch"))
                  }
                  className="shop-now-btn"
                >
                  VIEW ALL PRODUCTS
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="container section">
        <h2 className="section-title">TOP CATEGORIES</h2>
        <div className="category-grid">
          {categories.map((cat) => (
            <div key={cat._id} className="category-card">
              <div className="category-circle">
                <span>{cat.name[0]}</span>
              </div>
              <p>{cat.name}</p>
            </div>
          ))}
          {categories.length === 0 && <p>No categories found.</p>}
        </div>
      </section>
    </div>
  );
};

export default Home;
